import {ErrorMessage} from 'formik'
import moment from 'moment'
import React, {useState} from 'react'
import DatePicker from 'react-datepicker'

export const AUCTION_DATE_FORMAT = 'YYYY-MM-DD'
export const AUCTION_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

export const isValidAuctionDate = (value?: string | null) =>
  !!value && AUCTION_DATE_REGEX.test(value) && moment(value, AUCTION_DATE_FORMAT, true).isValid()

export const toAuctionDateString = (value: any) => {
  if (!value) return ''
  const date = moment(value)
  return date.isValid() ? date.format(AUCTION_DATE_FORMAT) : ''
}

interface Props {
  label: string
  name: string
  value?: string | null
  setFieldValue: (field: string, value: any) => void
  minDate?: string | null
  disabled?: boolean
  required?: boolean
  isClearable?: boolean
  placeholderText?: string
  'data-cy'?: string
}

/**
 * Date field that accepts typed input (digits and "-" only, YYYY-MM-DD) as well as calendar selection.
 * The Formik value is always kept as the raw YYYY-MM-DD string so invalid input can be reported by validation.
 */
const AuctionDateInput = React.memo((props: Props) => {
  const [openCalender, setOpenCalender] = useState<boolean>(false)
  const {
    label,
    name,
    value,
    setFieldValue,
    minDate,
    disabled,
    required,
    isClearable = true,
    placeholderText,
  } = props

  return (
    <div className='align-items-center row mb-2'>
      <label className='mb-1 fw-bolder fs-6 col-12'>
        <span className={required ? 'required' : ''}>{label}</span>
        <span className='text-muted fw-normal fs-7 ms-2'>(Format: YYYY-MM-DD)</span>
      </label>
      <div className='custom-datepicker col-12'>
        <div className='row'>
          <div className='col-lg-10' style={{paddingRight: 0}}>
            <DatePicker
              name={name}
              id={name}
              className='form-control pl-3 border-radius-right-0 pe-auto'
              autoComplete='off'
              placeholderText={placeholderText || 'YYYY-MM-DD'}
              dateFormat='yyyy-MM-dd'
              selected={isValidAuctionDate(value) ? moment(value, AUCTION_DATE_FORMAT).toDate() : null}
              value={value || ''}
              minDate={
                isValidAuctionDate(minDate)
                  ? moment(minDate, AUCTION_DATE_FORMAT).toDate()
                  : moment('1943-01-01').toDate()
              }
              disabled={disabled}
              open={openCalender}
              onClickOutside={() => setOpenCalender(false)}
              onInputClick={() => setOpenCalender(!openCalender)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.preventDefault()
              }}
              onChangeRaw={(event) => {
                // Also fired for calendar clicks; only handle typing in the input itself
                if (!(event?.target instanceof HTMLInputElement)) return
                // Typed input: keep only digits and "-", max 10 chars; validation reports bad formats
                event.preventDefault()
                const typed = event.target.value.replace(/[^0-9-]/g, '').slice(0, 10)
                setFieldValue(name, typed || null)
              }}
              onChange={(date: any) => {
                setOpenCalender(false)
                setFieldValue(name, date ? moment(date).format(AUCTION_DATE_FORMAT) : null)
              }}
              isClearable={isClearable}
              data-cy={props['data-cy']}
            />
          </div>
          <div
            className='input-group-append col-lg-2'
            onClick={() => !disabled && setOpenCalender(!openCalender)}
            style={{paddingLeft: 0}}
          >
            <span className='input-group-text input-group-text h-100 p-1 border-left-0 border-radius-left-0 bg-success text-white d-flex justify-content-center'>
              <i className='la la-calendar text-white' style={{fontSize: 20}} />
            </span>
          </div>
        </div>
        <div className='fv-plugins-message-container text-danger fw-bolder small text-end mt-1'>
          <ErrorMessage name={name} component='div' className='field-error-message' />
        </div>
      </div>
    </div>
  )
})

export default AuctionDateInput
